/**
 * DocumentMentionDropdown - Dropdown for @ mention document search
 */
import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useDocumentSearch } from "./hooks/use-document-search";
import { File02, Package } from "@untitledui/icons";
import type { Document } from "./hooks/use-document-search";

interface DocumentMentionDropdownProps {
    query: string;
    isOpen: boolean;
    anchorEl: HTMLElement | null;
    companyId: string;
    onSelect: (doc: Document) => void;
    onClose: () => void;
}

export function DocumentMentionDropdown({
    query,
    isOpen,
    anchorEl,
    companyId,
    onSelect,
    onClose,
}: DocumentMentionDropdownProps) {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
    const dropdownRef = useRef<HTMLDivElement>(null);

    const { data: documents = [], isLoading } = useDocumentSearch({
        query: query || "", // Empty string for recent docs
        companyId,
        enabled: isOpen,
        limit: query.length >= 2 ? 20 : 5, // 5 for recent, 20 for search
    });

    // Calculate dropdown position
    useEffect(() => {
        if (!isOpen || !anchorEl) return;

        const updatePosition = () => {
            const rect = anchorEl.getBoundingClientRect();
            const spaceAbove = rect.top;
            const spaceBelow = window.innerHeight - rect.bottom;
            const dropdownHeight = 320;

            setDropdownStyle({
                position: "fixed",
                left: `${rect.left}px`,
                width: `${Math.min(rect.width, 450)}px`,
                maxWidth: "450px",
                zIndex: 1000,
                ...(spaceAbove > dropdownHeight && spaceBelow < dropdownHeight
                    ? { bottom: `${window.innerHeight - rect.top + 8}px` }
                    : { top: `${rect.bottom + 8}px` }),
            });
        };

        updatePosition();
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition, true);

        return () => {
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition, true);
        };
    }, [isOpen, anchorEl]);

    // Reset selected index
    useEffect(() => {
        setSelectedIndex(0);
    }, [documents]);

    // Keyboard navigation
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            switch (e.key) {
                case "ArrowDown":
                    e.preventDefault();
                    setSelectedIndex((prev) =>
                        Math.min(prev + 1, documents.length - 1)
                    );
                    break;
                case "ArrowUp":
                    e.preventDefault();
                    setSelectedIndex((prev) => Math.max(prev - 1, 0));
                    break;
                case "Enter":
                    e.preventDefault();
                    if (documents[selectedIndex]) {
                        onSelect(documents[selectedIndex]);
                    }
                    break;
                case "Escape":
                    e.preventDefault();
                    onClose();
                    break;
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, documents, selectedIndex, onSelect, onClose]);

    // Auto-scroll
    useEffect(() => {
        if (!dropdownRef.current) return;

        const selectedElement = dropdownRef.current.querySelector(
            `[data-index="${selectedIndex}"]`
        );

        if (selectedElement) {
            selectedElement.scrollIntoView({
                block: "nearest",
                behavior: "smooth",
            });
        }
    }, [selectedIndex]);

    if (!isOpen || !anchorEl) return null;

    // Show header with @ indicator when query is empty
    const showRecentHeader = !query || query.length < 2;

    return (
        <AnimatePresence>
            <motion.div
                ref={dropdownRef}
                initial={{ opacity: 0, y: -8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.96 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
                style={dropdownStyle}
                className="max-h-80 overflow-hidden rounded-lg border-2 border-brand/30 bg-primary shadow-xl"
            >
                {/* Header with @ indicator */}
                {showRecentHeader && (
                    <div className="border-b border-secondary bg-brand/10 px-3 py-2">
                        <div className="flex items-center gap-2 text-sm">
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand/20 font-semibold text-brand">@</span>
                            <span className="font-medium text-primary">Documenti recenti</span>
                        </div>
                    </div>
                )}
                <div className="max-h-80 overflow-y-auto">
                    {isLoading ? (
                        <div className="flex items-center justify-center p-8">
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-tertiary border-t-brand" />
                            <span className="ml-3 text-sm text-tertiary">
                                Caricamento documenti...
                            </span>
                        </div>
                    ) : documents.length === 0 ? (
                        <div className="p-8 text-center">
                            <File02 className="mx-auto h-8 w-8 text-primary" />
                            <p className="mt-2 text-sm text-tertiary">
                                {query.length < 2
                                    ? "Digita almeno 2 caratteri per cercare"
                                    : "Nessun documento trovato"}
                            </p>
                        </div>
                    ) : (
                        <ul className="py-1">
                            {documents.map((doc, index) => (
                                <li
                                    key={doc.document_id}
                                    data-index={index}
                                    className={`cursor-pointer px-3 py-2.5 transition-colors ${
                                        index === selectedIndex
                                            ? "bg-secondary"
                                            : "hover:bg-secondary/50"
                                    }`}
                                    onClick={() => onSelect(doc)}
                                    onMouseEnter={() => setSelectedIndex(index)}
                                >
                                    <div className="flex items-start gap-3">
                                        {/* Icon */}
                                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-brand/10">
                                            <File02 className="h-4 w-4 text-primary" />
                                        </div>

                                        {/* Content */}
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate text-sm font-medium text-primary">
                                                {doc.file_name || "Untitled Document"}
                                            </div>

                                            <div className="mt-0.5 flex items-center gap-2 text-xs text-tertiary">
                                                {doc.document_type && (
                                                    <>
                                                        <span className="capitalize">
                                                            {doc.document_type.replace(/_/g, " ")}
                                                        </span>
                                                    </>
                                                )}
                                                {doc.document_number && (
                                                    <>
                                                        <span>•</span>
                                                        <span className="truncate">
                                                            {doc.document_number}
                                                        </span>
                                                    </>
                                                )}
                                                {doc.document_date && (
                                                    <>
                                                        <span>•</span>
                                                        <span>
                                                            {new Date(doc.document_date).toLocaleDateString("it-IT")}
                                                        </span>
                                                    </>
                                                )}
                                            </div>

                                            {/* Container info badge */}
                                            {doc.container && (
                                                <div className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-xs">
                                                    <Package className="h-3 w-3 text-tertiary" />
                                                    <span className="font-medium text-primary">
                                                        {doc.container.container_number}
                                                    </span>
                                                    {doc.container.container_type && (
                                                        <span className="text-tertiary">
                                                            ({doc.container.container_type})
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Footer */}
                {documents.length > 0 && (
                    <div className="border-t border-secondary bg-secondary/50 px-3 py-2">
                        <div className="flex items-center justify-between text-xs text-tertiary">
                            <div className="flex gap-3">
                                <span className="flex items-center gap-1">
                                    <kbd className="rounded bg-primary px-1.5 py-0.5 font-mono shadow-sm">
                                        ↑↓
                                    </kbd>
                                    Naviga
                                </span>
                                <span className="flex items-center gap-1">
                                    <kbd className="rounded bg-primary px-1.5 py-0.5 font-mono shadow-sm">
                                        ↵
                                    </kbd>
                                    Seleziona
                                </span>
                            </div>
                            <span className="flex items-center gap-1">
                                <kbd className="rounded bg-primary px-1.5 py-0.5 font-mono shadow-sm">
                                    Esc
                                </kbd>
                                Chiudi
                            </span>
                        </div>
                    </div>
                )}
            </motion.div>
        </AnimatePresence>
    );
}
